import { useState, useRef } from 'react'
import Box from '@mui/joy/Box'
import Typography from '@mui/joy/Typography'
import { TemplateItem } from '../components/TemplateItem'
import { ConfirmModal } from '../components/ui/ConfirmModal'
import { TemplateAPI } from '../api/TemplateAPI'
import { CreateNewTemplateButton } from '../components/CreateNewTemplateButton'
import { Loading } from '../components/ui/Loading'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export const TemplateLibrary = () => {
    const [isOpen, setIsOpen] = useState(false)
    const deleteId = useRef(null)
    const cloneId = useRef(null)
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const { data: templates } = useQuery({
        queryKey: ['templates'],
        queryFn: () => {
            return TemplateAPI.getOwned()
        },
    })

    const setForDelete = (id) => {
        deleteId.current = id
        setIsOpen(true)
    }

    const navToEditor = (id) => {
        navigate(`/merchant/template_library/${id}/edit`)
    }

    const navToPreview = (id) => {
        navigate(`/merchant/template_library/${id}/preview`)
    }

    const deleteTemplate = () => {
        deleteTemplateMutation.mutate(deleteId.current)
    }

    const deleteTemplateMutation = useMutation({
        mutationFn: (templateId) => {
            return TemplateAPI.delete(templateId)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['templates'] })
            setIsOpen(false)
        },
        onError: (e) => {
            console.log(e.message)
        },
    })
    const cloneTemplate = (id) => {
        cloneId.current = id
        cloneTemplateMutation.mutate(id)
    }

    const cloneTemplateMutation = useMutation({
        mutationFn: (templateId) => {
            return TemplateAPI.clone(templateId)
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['templates'] })
        },
    })

    let body = <Loading />
    if (templates) {
        if (templates.data.length > 0) {
            body = (
                <Box
                    sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: 2 }}
                >
                    {templates.data.map((template) => (
                        <TemplateItem
                            key={template.id}
                            template={template}
                            onDelete={setForDelete}
                            onPreview={navToPreview}
                            onEdit={navToEditor}
                            onClone={cloneTemplate}
                            clonePending={
                                cloneTemplateMutation.isPending &&
                                template.id === cloneId.current
                            }
                        />
                    ))}
                </Box>
            )
        } else {
            body = (
                <Box
                    sx={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Typography color="neutral" level="title-lg">
                        You don't have any templates. Try creating one!
                    </Typography>
                </Box>
            )
        }
    }
    return (
        <Box sx={{ display: 'flex', flex: 1, p: 2, backgroundColor: 'white' }}>
            <Box sx={{ flex: 0.5 }} />
            <Box
                sx={{
                    flex: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box sx={{ flex: 1, alignSelf: 'flex-end' }}>
                    <CreateNewTemplateButton />
                </Box>
                <Box sx={{ flex: 1, display: 'flex' }}>{body}</Box>
            </Box>
            <ConfirmModal
                title="Delete Template?"
                message="Are you sure you want to delete this item?"
                confirmLabel="Delete"
                cancelLabel="Cancel"
                isOpen={isOpen}
                onCancel={() => setIsOpen(false)}
                onConfirm={deleteTemplate}
            />
            <Box sx={{ flex: 0.5 }} />
        </Box>
    )
}
